/**
 * @fileoverview OpenAI Apps SDK Yardimci Fonksiyonlari
 *
 * Bu modul ChatGPT icindeki widget'tan OpenAI Apps SDK API'lerine
 * guvenli erisim icin yardimci fonksiyonlar saglar.
 *
 * window.openai nesnesi ChatGPT tarafindan saglanir ve asagidaki
 * API'leri icerir:
 * - callTool: MCP tool'larini cagirmak icin
 * - sendFollowUpMessage: Sohbete mesaj gondermek icin
 * - setWidgetState: Widget state'ini guncellemek icin
 * - requestDisplayMode: Goruntuleme modunu degistirmek icin
 * - openExternal: Harici URL acmak icin
 *
 * @module utils/openai
 * @see {@link https://platform.openai.com/docs/guides/apps-sdk} OpenAI Apps SDK Documentation
 *
 * @example
 * import { safeCallTool, isCallToolAvailable } from './openai';
 *
 * // Guvenli tool cagrisi:
 * if (isCallToolAvailable()) {
 *   const result = await safeCallTool('emlakjet.search_listings', {
 *     path: 'satilik-konut/istanbul'
 *   });
 * }
 */

import { ERROR_MESSAGES } from "./errors";

/**
 * OpenAI callTool API'sinin mevcut olup olmadigini kontrol eder.
 *
 * callTool API'si MCP tool'larini cagirmak icin kullanilir.
 * Development/sandbox ortamlarinda mevcut olmayabilir.
 *
 * @returns {boolean} callTool API'si mevcut ise true
 *
 * @example
 * if (isCallToolAvailable()) {
 *   await window.openai.callTool('emlakjet.search_listings', params);
 * } else {
 *   console.warn('callTool API not available');
 * }
 */
export function isCallToolAvailable() {
  return !!window.openai?.callTool;
}

/**
 * OpenAI sendFollowUpMessage API'sinin mevcut olup olmadigini kontrol eder.
 *
 * sendFollowUpMessage API'si sohbete programatik olarak
 * mesaj gondermek icin kullanilir (ornegin: "Bu ilan hakkinda
 * daha fazla bilgi ver" gibi follow-up sorular).
 *
 * @returns {boolean} sendFollowUpMessage API'si mevcut ise true
 *
 * @example
 * if (isSendMessageAvailable()) {
 *   window.openai.sendFollowUpMessage({
 *     prompt: 'Bu ilanin detaylarini goster'
 *   });
 * }
 */
export function isSendMessageAvailable() {
  return !!window.openai?.sendFollowUpMessage;
}

/**
 * MCP tool'unu guvenli sekilde cagirir.
 *
 * Bu fonksiyon:
 * - API mevcutluk kontrolu yapar
 * - Hata callback'i destekler
 * - API mevcut degilse null dondurur
 *
 * DIKKAT: Bu fonksiyon async'tir ve tool sonucunu bekler.
 * Tool cagrisi basarisiz olursa veya API mevcut degilse null doner.
 *
 * @param {string} toolName - Cagrilacak tool adi (ornegin: 'emlakjet.search_listings')
 * @param {Object} args - Tool'a gonderilecek parametreler
 * @param {Function} [onError] - Hata durumunda cagrilacak callback
 * @returns {Promise<Object|null>} Tool sonucu veya null (API mevcut degilse)
 *
 * @example
 * // Temel kullanim:
 * const result = await safeCallTool('emlakjet.search_listings', {
 *   path: 'satilik-konut/istanbul',
 *   limit: 20
 * });
 *
 * if (result) {
 *   setListings(result.listings);
 * }
 *
 * @example
 * // Hata callback ile:
 * const result = await safeCallTool(
 *   'emlakjet.get_listing_detail',
 *   { listingId: 12345 },
 *   (errorMsg) => setError(errorMsg)
 * );
 */
export async function safeCallTool(toolName, args, onError) {
  // API mevcutluk kontrolu
  if (!isCallToolAvailable()) {
    onError?.(ERROR_MESSAGES.CALL_TOOL_UNAVAILABLE);
    return null;
  }

  // Tool'u cagir ve sonucu dondur
  return window.openai.callTool(toolName, args);
}

/**
 * Sohbete guvenli sekilde follow-up mesaj gonderir.
 *
 * Bu fonksiyon:
 * - API mevcutluk kontrolu yapar
 * - API mevcut degilse false dondurur
 * - Basarili gonderimde true dondurur
 *
 * NOT: Bu fonksiyon senkron'dur ve mesajin gercekten
 * gonderilip gonderilmedigini beklemez. Sadece API
 * cagrisinin baslatilip baslatilmadigini dondurur.
 *
 * @param {Object} options - Mesaj secenekleri
 * @param {string} [options.prompt] - Sohbete gonderilecek mesaj metni
 * @returns {boolean} Mesaj gonderimi baslatildi ise true
 *
 * @example
 * // Follow-up soru gonderimi:
 * const sent = safeSendFollowUpMessage({
 *   prompt: 'Bu ilanin fiyat gecmisini goster'
 * });
 *
 * if (!sent) {
 *   console.warn('Could not send follow-up message');
 * }
 */
export function safeSendFollowUpMessage(options) {
  // API mevcutluk kontrolu
  if (!isSendMessageAvailable()) {
    return false;
  }

  // Mesaji gonder
  window.openai.sendFollowUpMessage(options);
  return true;
}
